/**
 * El catálogo de Vender sabiendo lo que hay en la cuenta.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  LO QUE PIDIÓ EL DUEÑO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Lo que ya está en la cuenta sube ARRIBA del catálogo, en verde y con cuántos
 * hay. Así el cajero ve de un vistazo qué lleva el cliente sin mirar a la otra
 * columna, y el «otro más de lo mismo» —el segundo refresco, la tercera
 * harina— está siempre a mano, arriba del todo.
 *
 * ⚠️ ORDENADO POR CANTIDAD, DE MÁS A MENOS. Primero se hizo en el orden en que
 * entraron a la cuenta, y el dueño lo corrigió con su ejemplo: «se tenía que
 * poner los 6 bolis y luego los de 2 y así». Lo que más lleva es lo que más
 * se repite, y es lo que tiene que estar más a mano.
 *
 * ⚠️ Y LA BÚSQUEDA SE RESPETA. Si está escrito «coca», la harina que está en la
 * cuenta NO aparece: no coincide con lo buscado. Solo sube lo que ya estaba en
 * la lista que se está viendo. Por eso esto reordena la lista YA FILTRADA, y no
 * añade nada que el filtro haya dejado fuera.
 *
 * Va aparte de `PosPage.tsx` para poder probarlo: dentro de un `useMemo` no se
 * puede.
 */

import { formatQty } from "@/lib/money";
import { etiquetaDeUnidad } from "./pesoYDinero";

/** Lo que hay de un producto en la cuenta. */
export type EnLaCuenta = {
  /** La suma de todas sus líneas: dos tallas de la misma camisa son dos. */
  cantidad: number;
  /**
   * En qué puesto entró a la cuenta. Solo desempata: con dos productos de la
   * misma cantidad, va primero el que entró antes. Sin desempate fijo, los dos
   * se cambiarían de sitio entre un toque y otro.
   */
  orden: number;
  /** `kg`, `l`… de sus líneas, para decir «1,5 kg» en vez de «1,5». */
  unidad: string | null;
  alPeso: boolean;
};

/** Las unidades que se venden partidas: ahí la cantidad lleva su unidad. */
const SE_PARTEN = new Set(["kg", "g", "l", "ml", "m"]);

/** Cuánto hay de cada producto en la cuenta, y en qué puesto entró. */
export function loQueHayEnLaCuenta(
  lineas: readonly {
    itemId: string;
    cantidad: number;
    unidad: string | null;
    permiteDecimales: boolean;
  }[],
): Map<string, EnLaCuenta> {
  const mapa = new Map<string, EnLaCuenta>();
  lineas.forEach((l, i) => {
    const ya = mapa.get(l.itemId);
    if (ya) {
      ya.cantidad += l.cantidad;
      return;
    }
    mapa.set(l.itemId, {
      cantidad: l.cantidad,
      orden: i,
      unidad: l.unidad,
      alPeso: l.permiteDecimales && SE_PARTEN.has((l.unidad ?? "").toLowerCase()),
    });
  });
  return mapa;
}

/**
 * La lista con lo de la cuenta primero, de más a menos cantidad (y a igual
 * cantidad, el que entró antes). El resto, exactamente como venía.
 *
 * ⚠️ Recibe la lista YA FILTRADA por la búsqueda y la categoría, y no añade
 * nada: solo cambia el orden. Así lo que la búsqueda dejó fuera sigue fuera.
 */
export function primeroLoDeLaCuenta<T extends { id: string }>(
  items: readonly T[],
  enLaCuenta: ReadonlyMap<string, EnLaCuenta>,
): T[] {
  if (enLaCuenta.size === 0) return items as T[];

  const arriba: T[] = [];
  const resto: T[] = [];
  for (const item of items) {
    (enLaCuenta.has(item.id) ? arriba : resto).push(item);
  }
  arriba.sort((a, b) => {
    const x = enLaCuenta.get(a.id)!;
    const y = enLaCuenta.get(b.id)!;
    return y.cantidad - x.cantidad || x.orden - y.orden;
  });
  return [...arriba, ...resto];
}

/** Lo que dice la píldora verde de la tarjeta: «2», «1,5 kg». */
export function cuantoDice(en: EnLaCuenta): string {
  const numero = formatQty(en.cantidad);
  if (!en.alPeso) return numero;
  const unidad = etiquetaDeUnidad(en.unidad);
  return unidad ? `${numero} ${unidad}` : numero;
}

// =============================================================================
//  LAS INICIALES DE LA TARJETA
// =============================================================================

/** Palabras que no dicen qué producto es: «Corte DE cabello» es «CC». */
const PALABRAS_VACIAS = new Set([
  "de", "del", "la", "el", "los", "las", "y", "e", "con", "sin", "para", "por",
  "en", "a", "al", "x", "p", "c",
]);

/**
 * Las dos letras del recuadro de la tarjeta: «BP» de «00011 BOLÍGRAFOS
 * PLATEADOS ELEGANTES».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ INICIALES Y NO UN DIBUJO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Antes cada tarjeta llevaba la misma cajita, que no decía nada — era igual en
 * los tres mil productos, y es la misma que usa cualquier otro sistema. Las
 * iniciales, en el color de su categoría, SÍ distinguen: en una fila de
 * bolígrafos, el «BP» se encuentra antes de terminar de leer.
 *
 * ⚠️ Se saltan los códigos. Los catálogos de verdad traen el código pegado
 * delante —«00011 BOLÍGRAFOS…», «001-12 COSMETIQUERA…»— y unas iniciales
 * «00» serían iguales en toda la papelería.
 */
export function iniciales(nombre: string): string {
  const palabras = nombre
    .split(/[\s\-_/.,;:()[\]«»"'+*#]+/u)
    .filter(
      (p) =>
        p.length > 0 &&
        /\p{L}/u.test(p) &&
        !/\d/.test(p) &&
        !PALABRAS_VACIAS.has(p.toLowerCase()),
    );

  if (palabras.length === 0) {
    // Solo códigos o palabras vacías: las primeras letras que haya, o nada.
    const letras = nombre.match(/\p{L}/gu) ?? [];
    return letras.slice(0, 2).join("").toUpperCase() || "#";
  }
  if (palabras.length === 1) {
    return [...palabras[0]!].slice(0, 2).join("").toUpperCase();
  }
  return ([...palabras[0]!][0]! + [...palabras[1]!][0]!).toUpperCase();
}

/**
 * El tinte del recuadro de las iniciales, con el color de la categoría.
 *
 * ⚠️ De fondo y borde, nunca de letra. Un color de categoría puede ser azul
 * marino, y en letra sobre la tarjeta oscura no se leería; de fondo suave, con
 * la letra en el color de siempre, se lee en los dos temas.
 */
export function tinteDeCategoria(
  color: string | null | undefined,
): { backgroundColor: string; borderColor: string } | null {
  if (!color) return null;
  let hex = color.trim();
  if (/^#[0-9a-f]{3}$/i.test(hex)) {
    hex = "#" + [...hex.slice(1)].map((c) => c + c).join("");
  }
  if (!/^#[0-9a-f]{6}$/i.test(hex)) return null;
  // 18 % de fondo y 35 % de borde, en hexadecimal: «2e» y «59».
  return { backgroundColor: `${hex}2e`, borderColor: `${hex}59` };
}
